import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { I18N, I18NContext, I18NConsumer } from 'commons';
import { orgId, Ajax, setPageTitle, qs } from 'utils';
import _ from 'lodash';
import ErrorBox from 'components/forms/TopErrorBox'
import Alert from 'commons/Alert'
import FormikFormControl from './formik-form-control';

import './forgot-password.scss';

const requiredSchema = Yup.string().required();
const emailSchema = Yup.string().email();
const { username } = qs();
export default function ForgotPassword(props) {
  const i18n = useContext(I18NContext);

  const [sendResult, setSendResult] = useState(null);

  const formRef = useRef(null);
  const usernameRef = useRef(null);
  const emailRef = useRef(null);
  const answerRef = useRef(null);

  const { orgName } = props;

  const getQuestion = (data) => {
    const url = `/${orgId}/forgotPassword/getQuestion`;
    return Ajax.post(url, data);
  };

  const forgotPassword = (data) => {
    const url = `/${orgId}/forgotPassword/validateForgetPassword`;
    return Ajax.post(url, data);
  };

  const sendEmail = (data) => {
    const url = `/${orgId}/forgotPassword/sendEmail`;
    return Ajax.post(url, data);
  };

  const validateUsernameAsync = async (loginId) => {
    const url = `/${orgId}/forgotPassword/validateUser?loginId=${loginId}`;
    const { data } = await Ajax.get(url);
    return data;
  };

  const validateUsername = async (values) => {
    let msg;
    const { username } = values;
    if (!await requiredSchema.isValid(username)) {
      msg = 'jquery.validation.field.required, ssp.register.username';
    } else {
      const result = await validateUsernameAsync(username);
      if (result === true) {
        const formData = new FormData();
        formData.append('loginId', username);
        getQuestion(formData).then(({ data: d }) => {
          const { question } = d;
          formRef.current.setFieldValue('question', question, false);
          setTimeout(() => {
            emailRef.current.setActived(true)
            answerRef.current.setActived(true)
          }, 500);
        });
      } else {
        msg = 'ssp.forgetpwd.username.error';
      }
    }
    return msg;
  };

  const validateEmailAsync = async (email, username) => {
    const url = `/${orgId}/forgotPassword/validateEmail?loginId=${username}&email=${encodeURIComponent(email)}`;
    const { data } = await Ajax.get(url);
    return data;
  };

  const validateEmail = async (values) => {
    let msg;
    const { username, email } = values;
    if (!await requiredSchema.isValid(email)) {
      msg = 'jquery.validation.field.required, ssp.register.registration.email';
    } else if (!await emailSchema.isValid(email)) {
      msg = 'jquery.validation.email';
    } else if (!await validateEmailAsync(email, username)) {
      msg = 'ssp.forgetpwd.email.error';
    }

    return msg;
  };

  const validateSecurityAnswerAsync = async (username, answer) => {
    const url = `/${orgId}/forgotPassword/validateQuestionAnswer`;
    const formData = new FormData();
    formData.append('loginId', username);
    formData.append('securityAnswer', answer);
    const { data, status } = await Ajax.post(url, formData);
    if(status != 200){
      return false;
    }
    return data;
  };

  const validateSecurityAnswer = async (values) => {
    let msg;
    const { username, answer } = values;
    if (!await requiredSchema.isValid(answer)) {
      msg = 'jquery.validation.field.required, ssp.register.answer';
    } else if (!await validateSecurityAnswerAsync(username, answer)) {
      msg = 'ssp.forgetpwd.security.answer.invalid';
    }

    return msg;
  };

  return (
    <div className="forgot-password-view container">
      <div className="ssp-well well forgot-password-form-container">
        <I18NConsumer>
          {() => setPageTitle(`${orgName} - ${i18n['ssp.title.forgotPassword']}`)}
        </I18NConsumer>
        <Formik
          innerRef={formRef}
          initialValues={{ username, email: '', question: '', answer: '' }}
          // validateOnChange={false}
          // validateOnBlur
          onSubmit={_.throttle((values, { setErrors, setSubmitting }) => {
            const { email: _email, username: loginId, question } = values;

            // handle the submission when user not input anything
            // validate fields before submitting
            const validatePromiseList = [validateUsername(values), validateEmail(values)]
            if (question) {
              validatePromiseList.push(validateSecurityAnswer(values))
            }
            Promise.all(validatePromiseList).then((res) => {
              const [usernameMsg, emailMsg, answerMsg] = res
              setErrors({ username: usernameMsg,
                email: emailMsg,
                ...question ? { answer: answerMsg } : {}
              })
              return !usernameMsg && !emailMsg && !answerMsg
            }).then((noError) => {
              if (noError) {
                // only submit when no errors found
                return Promise.resolve()
              }
              // reject when there are errors
              return Promise.reject()
            }).then(() => {
              const formData = new FormData();
              formData.append('email', _email);
              formData.append('loginId', loginId);
              formData.append('version', '201911');
              forgotPassword(formData).then(() => {
                sendEmail(formData).then(({ data: res }) => {
                  if (res.status == '1') {
                    setSendResult({
                      type: 'success',
                      msg: i18n['ssp.forgetpwd.send']
                    });
                    formRef.current.resetForm();
                  } else if (res.errorCode) {
                    const { fedrampLockTime } = res;
                    const errObj = {
                      type: 'danger',
                      msg: ''
                    };

                    if (fedrampLockTime) {
                      errObj.msg = i18n(res.errorCode, fedrampLockTime);
                    } else {
                      errObj.msg = i18n[res.errorCode];
                    }
                    setSendResult(errObj);
                  } else {
                    setSendResult({
                      type: 'danger',
                      msg: i18n['ssp.forgetpwd.send.error']
                    });
                  }
                }).catch(() => {
                  setSendResult({
                    type: 'danger',
                    msg: i18n['ssp.forgetpwd.send.error']
                  });
                });
              }).catch(() => {
                setSendResult({
                  type: 'danger',
                  msg: i18n['ssp.forgetpwd.send.error']
                });
              });
            }, () => {
              // End submission cycle
              setSubmitting(false)
            })
          }, 500)}
          >
            {({
            values,
            errors,
            touched,
            setFieldValue,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting
            }) => (
              <form className="forgot-password-form form-horizontal offset-md-1 col-md-11" eb-test="ssp_forgot_password_form">
                <div className="forgot-password-form-title">
                  <h1>
                    <I18N>ssp.title.forgotPassword</I18N>
                  </h1>
                  <p className="sub-title">
                    <I18N>forgot.forgotpsw</I18N>
                  </p>
                </div>
                <ErrorBox isSubmitting={isSubmitting} errors={errors} />
                <div>
                  {
                    sendResult &&
                    <div className="row">
                      <div className="col-md-8">
                        <Alert type={sendResult.type} message={sendResult.msg} role="alert" />
                      </div>
                    </div>
                  }
                  {
                    (sendResult && sendResult.type === 'success') ? '' :
                      <div>
                        <p className="text-muted">
                          <I18N>form.fields.are.required</I18N>
                        </p>
                        <FormikFormControl
                          labelI18nKey="ssp.register.username"
                          labelClass="required"
                          name="username"
                          type="text"
                          formValues={values}
                          onChange={(e) => {
                            if (values.question) {
                              setFieldValue('question', '', false)
                              setFieldValue('answer', '', false)
                            }
                            handleChange(e)
                          }}
                          onBlur={handleBlur}
                          error={errors.username}
                          errorRender={() => (
                            <div className="col-md-8 error" id="username-error" role="alert" aria-live="assertive">
                              <I18N>{errors.username}</I18N>
                            </div>
                          )}
                          touched={touched.username}
                          validate={formValues => {
                            if (values.email !== '') {
                              emailRef.current.setActived(true);
                            }
                            return validateUsername(formValues);
                          }}
                          innerRef={usernameRef}
                          eb-test="ssp_forgot_pwd_field_username"
                          aria-required
                          required
                        />
                        <FormikFormControl
                          labelI18nKey="ssp.register.registration.email"
                          labelClass="required"
                          name="email"
                          type="email"
                          formValues={values}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.email}
                          errorRender={() => (
                            <div className="col-md-8 error" id="email-error" role="alert" aria-live="assertive">
                              <I18N>{errors.email}</I18N>
                            </div>
                          )}
                          touched={touched.email}
                          validate={validateEmail}
                          innerRef={emailRef}
                          eb-test="ssp_forgot_pwd_field_email"
                          aria-required
                          required
                        />
                        {
                          values.question &&
                          <>
                            <div className="form-group row">
                              <div className="col-md-8">
                                <div className="col-form-label font-weight-bold" id="question-label">
                                  <I18N>ssp.register.question</I18N>
                                </div>
                                <div className="col-md-8 px-0">
                                  <span
                                    id="question"
                                    eb-test="ssp_forgot_pwd_field_question"
                                    aria-labelledby="question-label"
                                  >{i18n[values.question]}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <FormikFormControl
                              labelI18nKey="ssp.register.answer"
                              labelClass="required"
                              name="answer"
                              type="text"
                              innerRef={answerRef}
                              formValues={values}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.answer}
                              errorRender={() => (
                                <div className="col-md-8 error" id="answer-error" role="alert" aria-live="assertive">
                                  <I18N>{errors.answer}</I18N>
                                </div>
                              )}
                              touched={touched.answer}
                              validate={validateSecurityAnswer}
                              eb-test="ssp_forgot_pwd_field_answer"
                              aria-required
                              required
                            />
                          </>
                        }
                        <div className="form-group row">
                          <div className="offset-md-3 col-md-5 mt-3">
                            <button type="button" className="btn btn-primary btn-block" onClick={handleSubmit} eb-test="ssp_forgot_pwd_btn_send_mail">
                              <I18N>ssp.button.send</I18N>
                            </button>
                          </div>
                        </div>
                      </div>
                  }
                </div>
              </form>
            )}
          </Formik>
        
      </div>
    </div>
  );
}

ForgotPassword.propTypes = {
  orgName: PropTypes.string.isRequired
};
